import {
  Button,
  ButtonGroup,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
  Textarea,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Col } from 'styled-bootstrap-grid'
import { useDialog } from '@/context/dialogContext'
import { useAppSelector } from '@/redux/hooks'
import styled from 'styled-components'
import { formatDate } from '@/utils/date'
import commonUtils from '@/utils/commonUtils'
import { useRedis } from '@/hooks/useRedis'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

export const AuthorInfo = styled.div`
  width: 100%;
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
`

export const TextareaWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`

export const StyledTextarea = styled.textarea`
  flex: 1;
  padding: 25px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;

  &:disabled {
    color: #999;
    cursor: not-allowed;
  }
`

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 8px;
`

export const DtlButton = styled.button<{
  color?: string
  disabled?: boolean
  content?: string
  display?: string
}>`
  padding: 8px;
  margin-bottom: 4px;
  background-color: ${({ color }) => color || '#007bff'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  position: relative;
  display: ${({ display }) =>
    display || 'block'}; /* 버튼의 display 속성 설정 */

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.8)};
  }

  &::after {
    content: '${({ content }) => content || ''}';
    display: block;
    font-size: 12px;
    margin-top: 4px;
    color: white;
  }
`

interface NoticeDetailItem {
  isNew: true | false
  noticeSn: number
  lclasCd: string
  mlsfcCd: string
  sclasCd: string
  noticeSj: string
  noticeCn: string
  fileSeq: number
  readngCnt: number
  answerCnt: number
  tpNoticeYn: 'Y' | 'N'
  noticeFromDate: string
  noticeToDate: string
  popupYn: string
  popupFromDate: string
  popupToDate: string
  openYn: 'Y' | 'N'
  useYn: 'Y' | 'N'
  regId: string
  regDate: string
  updId: string
  updDate: string
}

const DetailNoticeMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAppSelector((state) => state.auth)
  const { showAlert, showConfirm } = useDialog()
  const { getComCodes, getComCodeValue } = useRedis()
  const [requestData] = useState({
    noticeSn: location.state?.noticeSn || '',
  })

  const [notice, setNotice] = useState<NoticeDetailItem>({
    isNew: !!requestData.noticeSn ? false : true,
    noticeSn: 0,
    lclasCd: '',
    mlsfcCd: '',
    sclasCd: '',
    noticeSj: '',
    noticeCn: '',
    fileSeq: 0,
    readngCnt: 0,
    answerCnt: 0,
    tpNoticeYn: 'N',
    noticeFromDate: '',
    noticeToDate: '',
    popupYn: 'N',
    popupFromDate: '',
    popupToDate: '',
    openYn: 'Y',
    useYn: 'Y',
    regId: !!requestData.noticeSn ? '' : user.userId,
    regDate: '',
    updId: '',
    updDate: '',
  })

  const [isVisible, setIsVisible] = useState<boolean>(false)
  const getNoticeDetail = async () => {
    try {
      const response = await axios.get('/noticeMgmt/detail', {
        params: { ...requestData },
      })
      const common = response.data.common
      if (common.resultCode == 'NOTICES0202') {
        const data = response.data.data
        data.notice.noticeFromDate = commonUtils.formatDate(
          data.notice.noticeFromDate,
          true,
        )
        data.notice.noticeToDate = commonUtils.formatDate(
          data.notice.noticeToDate,
          true,
        )
        setNotice(data.notice)
      } else {
        const closeFunc = () => {
          navigate('/noticeMgmt', {
            state: {
              srchData: location.state?.srchData,
              page: location.state?.page || 1,
              perPage: location.state?.page || 10,
            },
          })
        }
        showAlert({
          type: 'error',
          message: common.resultMsg,
          title: common.resultCode,
          onClose: closeFunc,
        })
      }
    } catch (error) {
      console.error('Error fetching terms info:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const name = e.target.name
    let value = e.target.value
    if (name === 'noticeFromDate') {
      value = value.replace(/\D/g, '')
      if (value.length >= notice.noticeFromDate.length) {
        // 백스페이스나 딜리트로 지우는 도중에는 포맷팅 제외
        value =
          value.length == 8
            ? commonUtils.formatDate(value + '000000', true)
            : value
      }
    } else if (name === 'noticeToDate') {
      value = value.replace(/\D/g, '')
      if (value.length >= notice.noticeToDate.length) {
        // 백스페이스나 딜리트로 지우는 도중에는 포맷팅 제외
        value =
          value.length == 8
            ? commonUtils.formatDate(value + '235959', true)
            : value
      }
    } else if (name === 'popupFromDate') {
      value = value.replace(/\D/g, '')
      if (value.length >= notice.popupFromDate.length) {
        // 백스페이스나 딜리트로 지우는 도중에는 포맷팅 제외
        value =
          value.length == 8
            ? commonUtils.formatDate(value + '000000', true)
            : value
      }
    } else if (name === 'popupToDate') {
      value = value.replace(/\D/g, '')
      if (value.length >= notice.popupToDate.length) {
        // 백스페이스나 딜리트로 지우는 도중에는 포맷팅 제외
        value =
          value.length == 8
            ? commonUtils.formatDate(value + '235959', true)
            : value
      }
    }
    setNotice((prevState) => {
      return {
        ...prevState,
        [name]: value,
        ['updId']: user.userId,
      }
    })
  }

  const updateNotice = async () => {
    try {
      if (!validationCheck()) {
        return
      }
      const sendData = { ...notice }
      if (notice.isNew) sendData['regId'] = user.userId
      notice.noticeFromDate = commonUtils
        .formatDate(notice.noticeFromDate, true)
        .substring(0, 10)
        .replaceAll('.', '')
      notice.noticeToDate = commonUtils
        .formatDate(notice.noticeToDate, true)
        .substring(0, 10)
        .replaceAll('.', '')
      notice.popupFromDate = commonUtils
        .formatDate(notice.popupFromDate, true)
        .substring(0, 10)
        .replaceAll('.', '')
      notice.popupToDate = commonUtils
        .formatDate(notice.popupToDate, true)
        .substring(0, 10)
        .replaceAll('.', '')
      const response = await axios.post(
        notice.isNew ? '/noticeMgmt/insertNotice' : '/noticeMgmt/noticeUpdate',
        notice,
      )
      const common = response.data.common
      if (common.resultCode == 'NOTICEI0200')
        showAlert({
          type: 'success',
          message: '정상적으로 생성되었습니다.',
          title: '성공',
          onClose() {
            navigate('/noticeMgmt', {
              state: {
                srchData: location.state?.srchData,
                page: location.state?.page || 1,
                perPage: location.state?.page || 10,
              },
            })
          },
        })
      else if (common.resultCode == 'NOTICEU0200')
        showAlert({
          type: 'success',
          message: '정상적으로 수정되었습니다.',
          title: '성공',
          onClose() {
            navigate('/noticeMgmt', {
              state: {
                srchData: location.state?.srchData,
                page: location.state?.page || 1,
                perPage: location.state?.page || 10,
              },
            })
          },
        })
      else
        showAlert({
          type: 'error',
          message: common.resultMsg || '공지사항 업데이트 실패',
          title: common.resultCode,
        })
    } catch (error) {
      console.error('Error fetching terms reg:', error)
    }
  }

  const validateDateFormat = (str: string): boolean => {
    const datePattern = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/
    return datePattern.test(str)
  }

  const validationCheck = (): boolean => {
    let retVal = true
    let msg = ''
    let target = ''
    const notiStartDt = commonUtils
      .formatDate(notice.noticeFromDate, true)
      .substring(0, 10)
      .replaceAll('.', '')
    const notiEndDt = commonUtils
      .formatDate(notice.noticeToDate, true)
      .substring(0, 10)
      .replaceAll('.', '')
    const popupStartDt = commonUtils
      .formatDate(notice.popupFromDate, true)
      .substring(0, 10)
      .replaceAll('.', '')
    const popupEndDt = commonUtils
      .formatDate(notice.popupToDate, true)
      .substring(0, 10)
      .replaceAll('.', '')

    if (notice.noticeSj.trim().length == 0) {
      retVal = false
      msg = '공지사항 제목을 확인해주세요.'
      target = 'noticeSj'
    } else if (notice.noticeCn.trim().length == 0) {
      retVal = false
      msg = '공지사항 내용을 확인해주세요.'
      target = 'noticeCn'
    } else if (notiStartDt.length != 8 || !validateDateFormat(notiStartDt)) {
      retVal = false
      msg = '공지사항 게시 시작일을 확인해주세요.'
      target = 'noticeFromDate'
    } else if (notiEndDt.length != 8 || !validateDateFormat(notiEndDt)) {
      retVal = false
      msg = '공지사항 게시 종료일을 확인해주세요.'
      target = 'noticeToDate'
    } else if (popupStartDt.length != 8 || !validateDateFormat(popupStartDt)) {
      retVal = false
      msg = '팝업 게시 시작일을 확인해주세요.'
      target = 'popupFromDate'
    } else if (popupEndDt.length != 8 || !validateDateFormat(popupEndDt)) {
      retVal = false
      msg = '팝업 게시 종료일을 확인해주세요.'
      target = 'popupToDate'
    }
    if (msg != '')
      showAlert({
        type: 'warning',
        message: msg,
        onClose() {
          document.getElementById(target)?.focus()
        },
      })
    return retVal
  }

  const toggleVisibility = () => {
    setIsVisible((prevState) => !prevState)
  }

  useEffect(() => {
    if (!notice.isNew) {
      getNoticeDetail()
    }
  }, [])

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>공지게시판 관리</p>
          <div style={{ display: 'flex', gap: '5px' }}>
            {!notice.isNew ? (
              <Button
                variant="outlined"
                color="neverbe"
                onClick={toggleVisibility}
              >
                {isVisible ? '접기' : '전체보기'}
              </Button>
            ) : (
              <></>
            )}
            <Button
              onClick={() =>
                showConfirm({
                  title: '공지목록 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/noticeMgmt')
                  },
                })
              }
              color="neverbe"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '공지사항을 저장할까요?',
                  onConfirm() {
                    updateNotice()
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
          </div>
        </div>
      </div>
      <div>
        {isVisible && (
          <Row>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="lclasCd">
                  게시물 대분류 코드
                </Label>
                <Input
                  id="lclasCd"
                  name="lclasCd"
                  type="text"
                  maxLength={250}
                  value={notice.lclasCd}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="mlsfcCd">
                  게시물 중분류 코드
                </Label>
                <Input
                  id="mlsfcCd"
                  name="mlsfcCd"
                  type="text"
                  maxLength={250}
                  value={notice.mlsfcCd}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="sclasCd">
                  게시물 소분류 코드
                </Label>
                <Input
                  id="sclasCd"
                  name="sclasCd"
                  type="text"
                  maxLength={250}
                  value={notice.sclasCd}
                  disabled={true}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="noticeFromDate">
                공지 게시 시작일(YYYYMMDD)
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="noticeFromDate"
                name="noticeFromDate"
                type="text"
                maxLength={8}
                value={notice?.noticeFromDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="noticeToDate">
                공지 게시 종료일(YYYYMMDD)
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="noticeToDate"
                name="noticeToDate"
                type="text"
                maxLength={8}
                value={notice.noticeToDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="useYn">
                사용여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                value={notice.useYn}
                onChange={handleInputChange}
              >
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="openYn">
                공개여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="openYn"
                name="openYn"
                value={notice.openYn}
                onChange={handleInputChange}
              >
                {getComCodes('OPEN_YN').map((item) => (
                  <option key={'openYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="tpNoticeYn">
                상단고정여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="tpNoticeYn"
                name="tpNoticeYn"
                value={notice.tpNoticeYn}
                onChange={handleInputChange}
              >
                {getComCodes('TP_NOTICE_YN').map((item) => (
                  <option key={'tpNoticeYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="popupYn">
                팝업노출 여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="popupYn"
                name="popupYn"
                value={notice.popupYn}
                onChange={handleInputChange}
              >
                {getComCodes('POPUP_YN').map((item) => (
                  <option key={'popupYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="popupFromDate">
                팝업노출 시작일(YYYYMMDD)
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="poupFromDate"
                name="popupFromDate"
                type="text"
                maxLength={8}
                value={notice?.popupFromDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="popupToDate">
                팝업노출 종료일(YYYYMMDD)
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="popupToDate"
                name="popupToDate"
                type="text"
                maxLength={8}
                value={notice.popupToDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="noticeSj">
                제목
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="noticeSj"
                name="noticeSj"
                type="text"
                maxLength={255}
                value={notice.noticeSj}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="noticeCn">
                공지 내용
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Textarea
                id="noticeCn"
                name="noticeCn"
                value={notice.noticeCn}
                height={'300px'}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        {!notice.isNew ? (
          <Row>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="regDate">
                  작성일
                </Label>
                <div style={{ marginBottom: '5px' }} />
                <Input
                  id="regDate"
                  name="regDate"
                  type="text"
                  placeholder=""
                  maxLength={3}
                  value={commonUtils.formatDate(notice.regDate, true)}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="updDate">
                  최종 수정 일시
                </Label>
                <div style={{ marginBottom: '5px' }} />
                <Input
                  id="updDate"
                  name="updDate"
                  type="text"
                  placeholder=""
                  maxLength={100}
                  value={commonUtils.formatDate(notice.updDate, true)}
                  disabled={true}
                />
              </FormGroup>
            </Col>
          </Row>
        ) : (
          <></>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '5px' }}>
            <Button
              onClick={() =>
                showConfirm({
                  title: '공지목록 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/noticeMgmt')
                  },
                })
              }
              color="neverbe"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '공지사항을 저장할까요?',
                  onConfirm() {
                    updateNotice()
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
export default DetailNoticeMgmt
